/**
 * PaymentChoiceModal · iter266
 * Einheitliche Zahlungsart-Auswahl (Stripe/PayPal/Vorkasse) — gleiche aktive
 * Methoden wie Warenkorb/Admin. Verwendet für Abo & Einzelkauf.
 * onConfirm(method) muss ein Objekt zurückgeben:
 *   { done:true }              → Erfolg (Mock-Freischaltung)
 *   { bank_instructions:{…} }  → Vorkasse-Bankdaten anzeigen
 *   (redirect passiert im Aufrufer)
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Loader2, X, CheckCircle } from "lucide-react";

const LABEL = { STRIPE: "Kreditkarte (Stripe)", PAYPAL: "PayPal", BANK_TRANSFER: "Überweisung / Vorkasse" };

export default function PaymentChoiceModal({ open, title, amount, onClose, onConfirm, allowedMethods, notice }) {
  const { t } = useTranslation();
  const [methods, setMethods] = useState([]);
  const [method, setMethod] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [bank, setBank] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErr(""); setBank(null); setDone(false);
    (async () => {
      try {
        const { data } = await api.get("/payments/methods");
        let l = (data?.methods || data || []).filter((m) => m.enabled !== false);
        if (Array.isArray(allowedMethods) && allowedMethods.length) {
          l = l.filter((m) => allowedMethods.includes(m.code));
        }
        setMethods(l);
        setMethod((l[0] && l[0].code) || (allowedMethods?.[0]) || "STRIPE");
      } catch { setMethods([{ code: "STRIPE" }]); setMethod("STRIPE"); }
    })();
  }, [open]);

  if (!open) return null;

  const go = async () => {
    setBusy(true); setErr("");
    try {
      const r = await onConfirm(method);
      if (r?.bank_instructions) { setBank(r.bank_instructions); return; }
      if (r?.done) { setDone(true); return; }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr((typeof d === "string" ? d : d?.message) || "Zahlung fehlgeschlagen.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="payment-choice-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-sm w-full p-5">
        <div className="flex justify-between items-start mb-3">
          <h3 className="text-base font-bold text-[#0A0A0A]">{title}</h3>
          <button onClick={onClose} data-testid="payment-choice-close"><X size={18} /></button>
        </div>
        {amount != null && <p className="text-sm text-[#666] mb-3">Betrag: <strong>{Number(amount).toFixed(2)} €</strong></p>}
        {notice && !done && !bank && (
          <p data-testid="payment-choice-notice" className="text-xs text-[#666] bg-[#F5F5F5] border border-[#E5E5E5] rounded-lg p-2.5 mb-3 leading-relaxed">
            {notice}
          </p>
        )}
        {done ? (
          <div data-testid="payment-choice-success" className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800 flex items-center gap-2">
            <CheckCircle size={16} /> Zahlung erfolgreich — freigeschaltet!
          </div>
        ) : bank ? (
          <div data-testid="payment-choice-bank" className="text-xs text-[#333] space-y-1">
            <p className="font-semibold text-[#0A0A0A]">{t("payment_choice.transfer_data")}</p>
            <p>IBAN: <b>{bank.iban || "—"}</b></p>
            <p>BIC: <b>{bank.bic || "—"}</b></p>
            <p>Verwendungszweck: <b>{bank.reference}</b></p>
            <p>Betrag: <b>{Number(bank.amount).toFixed(2)} €</b></p>
            <p className="text-[#999]">Freischaltung erfolgt nach Zahlungseingang.</p>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-2 mb-3">
              {methods.map((m) => (
                <button key={m.code} data-testid={`pay-method-${m.code}`} onClick={() => setMethod(m.code)}
                  className={`h-11 rounded-xl border px-4 text-sm font-semibold text-left transition-colors ${method === m.code ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] text-[#0A0A0A] hover:bg-[#F5F5F5]"}`}>
                  {LABEL[m.code] || m.label || m.code}
                </button>
              ))}
            </div>
            {err && <p className="text-xs text-red-600 mb-2" data-testid="payment-choice-error">{err}</p>}
            <button data-testid="payment-choice-confirm" disabled={busy || !method} onClick={go}
              className="w-full h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-bold disabled:opacity-40 flex items-center justify-center gap-2 hover:bg-[#222]">
              {busy && <Loader2 size={14} className="animate-spin" />}
              {method === "BANK_TRANSFER" ? "Überweisungsdaten anzeigen" : "Jetzt zahlen"}
            </button>
          </>
        )}
        {(done || bank) && (
          <button onClick={onClose} data-testid="payment-choice-done-close" className="w-full h-10 mt-3 rounded-full border border-[#E5E5E5] text-sm hover:bg-[#F5F5F5]">{t("common.close")}</button>
        )}
      </div>
    </div>
  );
}
